import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Dense form primitives for ERP operator screens.
 * Prefer `variant="plain"` on documents to avoid nested-card weight.
 */

export function FormSection({
  id,
  title,
  description,
  icon,
  aside,
  children,
  className,
  variant = "card",
}: {
  id?: string;
  title: string;
  description?: string;
  icon?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  /** card = bordered panel; plain = hairline header only (documents) */
  variant?: "card" | "plain";
}) {
  const isPlain = variant === "plain";
  return (
    <section
      id={id}
      className={cn(
        "scroll-mt-20",
        isPlain ? "border-b border-border/80 pb-2" : "rounded-md border border-border bg-card",
        className,
      )}
    >
      <header
        className={cn(
          "flex items-center justify-between gap-2",
          isPlain
            ? "mb-1.5 px-0 py-1"
            : "border-b border-border/70 px-[var(--fs-section-px)] py-1.5",
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          {icon ? <span className="text-muted-foreground [&_svg]:size-3.5">{icon}</span> : null}
          <div className="min-w-0">
            <h2 className="truncate text-xs font-semibold tracking-[-0.005em]">{title}</h2>
            {description ? (
              <p className="truncate text-2xs text-muted-foreground">{description}</p>
            ) : null}
          </div>
        </div>
        {aside}
      </header>
      <div
        className={cn(
          isPlain ? "px-0 py-0.5" : "px-[var(--fs-section-px)] py-[var(--fs-section-py)]",
        )}
      >
        {children}
      </div>
    </section>
  );
}

/** 12-column grid; children pick span via Field `span`. */
export function FieldGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 sm:grid-cols-12",
        "gap-x-[var(--fs-field-gap-x)] gap-y-[var(--fs-field-gap-y)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  span = 4,
  children,
  className,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  span?: 2 | 3 | 4 | 5 | 6 | 7 | 8 | 12;
  children: ReactNode;
  className?: string;
}) {
  const spanClass = {
    2: "sm:col-span-2",
    3: "sm:col-span-3",
    4: "sm:col-span-4",
    5: "sm:col-span-5",
    6: "sm:col-span-6",
    7: "sm:col-span-7",
    8: "sm:col-span-8",
    12: "sm:col-span-12",
  }[span];

  return (
    <div className={cn("min-w-0 space-y-0.5", spanClass, className)}>
      <label htmlFor={htmlFor} className="field-label flex items-center gap-1">
        {label}
        {required ? <span className="text-destructive">*</span> : null}
      </label>
      {children}
      {error ? (
        <p className="text-2xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-2xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

/** Checkbox-style boolean rendered as a bordered row so it aligns with inputs. */
export function ToggleField({
  label,
  description,
  checked,
  onChange,
  span = 4,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  span?: 3 | 4 | 6 | 12;
}) {
  const spanClass = {
    3: "sm:col-span-3",
    4: "sm:col-span-4",
    6: "sm:col-span-6",
    12: "sm:col-span-12",
  }[span];

  return (
    <label
      className={cn(
        "flex cursor-pointer select-none items-start gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5 transition-colors hover:border-border-strong",
        spanClass,
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-3.5 shrink-0 accent-[var(--primary)]"
      />
      <span className="min-w-0">
        <span className="block text-xs font-medium leading-tight">{label}</span>
        {description ? (
          <span className="mt-0.5 block text-2xs leading-tight text-muted-foreground">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/** Read-only label/value pair used across detail views. */
export function DataItem({
  label,
  children,
  span = 3,
  mono,
}: {
  label: string;
  children: ReactNode;
  span?: 2 | 3 | 4 | 5 | 6 | 12;
  mono?: boolean;
}) {
  const spanClass = {
    2: "sm:col-span-2",
    3: "sm:col-span-3",
    4: "sm:col-span-4",
    5: "sm:col-span-5",
    6: "sm:col-span-6",
    12: "sm:col-span-12",
  }[span];

  return (
    <div className={cn("min-w-0 space-y-0.5", spanClass)}>
      <dt className="field-label">{label}</dt>
      <dd className={cn("truncate text-sm", mono && "font-mono text-xs")}>{children}</dd>
    </div>
  );
}
